import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { submitChangeRequest } from '@/data/changeRequests';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Loader2, Send, AlertCircle } from 'lucide-react';

interface ChangeRequestFormProps {
  productId: string;
  productTitle: string;
  onSuccess?: () => void;
}

export function ChangeRequestForm({ productId, productTitle, onSuccess }: ChangeRequestFormProps) {
  const [changeNotes, setChangeNotes] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const queryClient = useQueryClient();

  const submitMutation = useMutation({
    mutationFn: () => submitChangeRequest(productId, changeNotes, videoUrl || undefined),
    onSuccess: () => {
      toast.success('Change request submitted successfully');
      queryClient.invalidateQueries({ queryKey: ['vendor-change-requests'] });
      setChangeNotes('');
      setVideoUrl('');
      onSuccess?.();
    },
    onError: (error: any) => {
      toast.error('Failed to submit: ' + error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (changeNotes.length < 20) {
      toast.error('Please provide at least 20 characters of detail');
      return;
    }
    submitMutation.mutate();
  };

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Request Changes</h2>
        <p className="text-sm text-muted-foreground">
          For product: <strong>{productTitle}</strong>
        </p>
      </div>

      <Alert className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Describe the changes you'd like to make to your listing. Our AI will analyze your request and our team will review it.
        </AlertDescription>
      </Alert>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Label htmlFor="change_notes">
            Change Description <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="change_notes"
            value={changeNotes}
            onChange={(e) => setChangeNotes(e.target.value)}
            rows={8}
            placeholder="Please describe in detail what changes you'd like to make to your listing. Include specific information about text updates, image changes, feature additions, etc."
            className="resize-none"
            required
          />
          <p className="text-xs text-muted-foreground mt-1">
            Minimum 20 characters. Be as detailed as possible.
          </p>
        </div>

        <div>
          <Label htmlFor="video_url">Loom Video URL (Optional)</Label>
          <Input
            id="video_url"
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://www.loom.com/share/..."
          />
          <p className="text-xs text-muted-foreground mt-1">
            Share a Loom video to provide additional context or show specific changes you'd like.
          </p>
        </div>

        <Button
          type="submit"
          disabled={submitMutation.isPending || changeNotes.length < 20}
          className="w-full"
        >
          {submitMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Submit Change Request
            </>
          )}
        </Button>
      </form>
    </Card>
  );
}
